import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { Player } from 'modules/players/domain/interfaces/player';
import { usePlayerList } from 'modules/players/ui/hooks/use-player-list';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { getBirthDay, getFullName } from 'shared/utils';
import UDButton from 'ud-ui/components/button';
import HeaderContentCategory from 'ud-ui/components/category/components/list-wrapper/header-content';
import Modal from 'ud-ui/components/modal';
import { UDEntityRow, UDPagedList } from 'ud-ui/components/paged-list';

const SEARCH_PLACEHOLDER = 'Поиск по игрокам';
const CREATE_BUTTON_TEXT = 'Добавить игрока';

function PlayerListPage() {
  const navigate = useNavigate();
  const { setHeaderContent } = useMainLayout();

  const handleCreateClick = useCallback(() => {
    navigate(Pages.PLAYERS.CREATE);
  }, [navigate]);

  const {
    items,
    itemsOffset,
    state,
    page,
    totalPages,
    perPage,
    draftSearch,
    setPage,
    setPerPage,
    setSearch,
    buildPageHref,
    deletePlayer,
  } = usePlayerList();

  const [playerToDelete, setPlayerToDelete] = useState<Player | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const openDelete = useCallback((player: Player) => {
    setPlayerToDelete(player);
  }, []);
  const closeDelete = useCallback(() => {
    setPlayerToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!playerToDelete) return;
    setIsDeleting(true);
    try {
      await deletePlayer(playerToDelete.id);
      setPlayerToDelete(null);
      toast.success('Игрок успешно удалён');
    } catch {
      toast.error('Не удалось удалить игрока');
    } finally {
      setIsDeleting(false);
    }
  }, [deletePlayer, playerToDelete]);

  const handlePlayerClick = useCallback(
    (player: Player) => {
      navigate(Pages.PLAYERS.builders.view(player.id));
    },
    [navigate],
  );

  useEffect(() => {
    setHeaderContent(
      <HeaderContentCategory title="Игроки" createButtonText={CREATE_BUTTON_TEXT} createButtonOnClick={handleCreateClick} />,
    );
    return () => setHeaderContent(null);
  }, [handleCreateClick, setHeaderContent]);

  // Номер строки продолжается со страницы на страницу и считается от `itemsOffset`,
  // а не от `page`: пока едет следующая страница, на экране ещё прошлые элементы.
  const renderPlayer = useCallback(
    (player: Player, index: number) => (
      <UDEntityRow
        index={itemsOffset + index + 1}
        avatar={player.photo?.externalUrl ?? USER_DEFAULT_AVATAR}
        title={getFullName(player)}
        subtitle={
          <span className="flex gap-4">
            <span>{getBirthDay({ birthDate: player.birthDate ? new Date(player.birthDate) : undefined })}</span>
            <span>Рост: {player.height ? `${player.height} см` : '-'}</span>
            <span>Вес: {player.weight ? `${player.weight} кг` : '-'}</span>
          </span>
        }
        actions={
          <UDButton variant="secondary" style={{ width: 144 }} onClick={() => openDelete(player)}>
            Удалить
          </UDButton>
        }
        onClick={() => handlePlayerClick(player)}
      />
    ),
    [handlePlayerClick, itemsOffset, openDelete],
  );

  return (
    <>
      <UDPagedList<Player>
        items={items}
        renderItem={renderPlayer}
        getItemKey={(player) => player.id}
        state={state}
        page={page}
        totalPages={totalPages}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={setPerPage}
        pageHref={buildPageHref}
        search={draftSearch}
        onSearchChange={setSearch}
        searchPlaceholder={SEARCH_PLACEHOLDER}
        emptyText="Игроков пока нет"
        emptyAction={
          <UDButton variant="primary" onClick={handleCreateClick}>
            {CREATE_BUTTON_TEXT}
          </UDButton>
        }
        notFoundText="Игроки не найдены"
      />

      <Modal.Confirm
        show={Boolean(playerToDelete)}
        title={`Удалить игрока «${playerToDelete ? getFullName(playerToDelete) : ''}»?`}
        description="Это действие необратимо."
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={closeDelete}
      />
    </>
  );
}

export default PlayerListPage;
